import api from "./axios";

// Module 12 — Notifications. EIGHTEEN routes in `functions/src/routers/notificationRoutes.js`,
// mounted at `/notifications`. The backend is FROZEN: nothing here adds, renames or reshapes an
// endpoint.
//
// Route gates, all composed after `protect → requirePasswordChanged → requireActive`:
//   ownerGate       = requireAuthentication   — the inbox and preferences. Reachable by a CUSTOMER,
//                     so it cannot be a department or designation gate; WHOSE record it is, is
//                     decided in the service on every call.
//   staffActionGate = requireDesignation(all four staff designations) — the retry route's outer half.
//                     The binding check is the service's, which is MASTER ADMIN ONLY.
//   masterOnlyGate  = requireDesignation(Master Admin) — the catalogue, the configuration, all three
//                     oversight reads, and the Module 13 contract read.
//
// ── SIX RULES THAT SHAPE THIS MODULE ────────────────────────────────────────
// 1. NOTHING IS EVER DELETED. There is no DELETE route anywhere. Archive is a STATE at the end of the
//    lifecycle, not a removal — an archived notification keeps its channels, its delivery outcomes
//    and its read state, and stays reportable.
// 2. READ AND ARCHIVE ARE FORWARD-ONLY AND IRREVERSIBLE BY ANY ACTOR. A Master Admin may READ another
//    user's delivery status but may never mark their notification read: read status must stay
//    auditable, and reversing it would falsify it.
// 3. THE UNREAD COUNT IS DERIVED ON EVERY CALL. No counter is stored anywhere in the module.
// 4. THERE IS NO AUTOMATIC RETRY. `Failed` is terminal to every automatic path; the single manual
//    re-queue preserves the attempt budget rather than resetting it, so repetition cannot buy an
//    unbounded one.
// 5. OVERSIGHT IS MASTER ADMIN ONLY — a Department Admin has NO scope over notifications at all.
//    Preferences are personal for every role without exception.
// 6. SUPPRESSION IS CUSTOMER-FACING ONLY and is INFERRED, never asserted by the emitting module. A
//    staff inbox is never suppressed, so nothing is ever withheld from one.

// ── The inbox (EP-01 … EP-06) — the caller's OWN notifications only ─────────

/**
 * The caller's own inbox, server-paged.
 * Filters: `readState`, `archiveState`, `priority`, `page`, `limit` (max 100, default 20).
 *
 * A notification is visible when its In-App channel is not Suppressed, Skipped or Held. For a
 * CUSTOMER, messages still inside their suppression-adjudication window are additionally withheld
 * until that window closes — so a subordinate message is never shown and then retracted.
 */
export const fetchMyNotifications = (params) =>
  api.get("/notifications", { params }).then((r) => r.data.data);

/** DERIVED on every call, never a stored counter. Same visibility predicate as the listing. */
export const fetchUnreadCount = () =>
  api.get("/notifications/unread-count").then((r) => r.data.data);

/** One of the caller's own. A notification they cannot see answers 404, never 403. */
export const fetchMyNotification = (id) =>
  api.get(`/notifications/${id}`).then((r) => r.data.data);

/** Forward-only. Re-marking an already-read notification is a no-op returning `changed: false`. */
export const markRead = (id) =>
  api.patch(`/notifications/${id}/read`).then((r) => r.data.data);

/** Scoped by the SAME visibility filter as the listing — nothing unseen is swept up. */
export const markAllRead = () =>
  api.patch("/notifications/read-all").then((r) => r.data.data);

/** Archive is a STATE, never a delete. Forward-only; re-archiving is a no-op. */
export const archiveNotification = (id) =>
  api.patch(`/notifications/${id}/archive`).then((r) => r.data.data);

// ── Preferences (EP-07 / EP-08) — self-scoped for EVERY role ───────────────

/**
 * The caller's own preferences, lazily created empty on first read.
 * Returns `{ preference, scope }` — `scope` explains what is NOT configurable and why.
 */
export const fetchMyPreferences = () =>
  api.get("/notifications/preferences").then((r) => r.data.data);

/**
 * `{ channelPreferences?, quietHours? }` and nothing else — an unknown key is REFUSED, not ignored.
 * In-App and Push are refused with a reason; a per-user timezone is refused with a pointer to the
 * single company timezone. `quietHours: null` is an explicit clear.
 */
export const updateMyPreferences = (body) =>
  api.patch("/notifications/preferences", body).then((r) => r.data.data);

// ── Catalogue (EP-09 … EP-11) — Master Admin ───────────────────────────────

/**
 * The seventy rows and their totals. Filters: `priority`, `suppressionClass`.
 * ⚠ `awaitingModule13` is NOT sent — see UNUSABLE_FILTERS below.
 */
export const fetchCatalogue = (params) =>
  api.get("/notifications/catalogue", { params }).then((r) => r.data.data);

/** One row, addressed by its frozen event name. */
export const fetchCatalogueRow = (eventName) =>
  api.get(`/notifications/catalogue/${encodeURIComponent(eventName)}`).then((r) => r.data.data);

/**
 * ROUTING ONLY, with a mandatory `changeReason`. The event name is fixed by frozen code and the
 * service refuses to change it. Accepts `recipientGroups`, `recipientsUnassigned`, `channels`,
 * `templateKeys`, `priority`, `suppressionClass`, `personalClass`, `mandatory`, `awaitingModule13`.
 *
 * Setting `priority` also clears `prioritySeeded` — a ruled priority is no longer the seed's guess.
 */
export const updateCatalogueRow = (eventName, body) =>
  api
    .patch(`/notifications/catalogue/${encodeURIComponent(eventName)}`, body)
    .then((r) => r.data.data);

// ── Configuration (EP-12 / EP-13) — Master Admin ───────────────────────────

/** Every configuration item with whether it is set, what happens instead, and the open ruling. */
export const fetchConfiguration = () =>
  api.get("/notifications/config").then((r) => r.data.data);

/**
 * `{ changeReason, ...items }`. CFG-06 is REFUSED here by design: the `mandatory` flag on the
 * catalogue row is the single source of truth and is set through the catalogue, not the config.
 */
export const updateConfiguration = (body) =>
  api.patch("/notifications/config", body).then((r) => r.data.data);

// ── Oversight (EP-14 … EP-17) — Master Admin ───────────────────────────────

/**
 * Every notification event, server-paged. Filters: `eventName`, `state`, `correlationKey`.
 *
 * This is how a routing defect is FOUND rather than merely recorded: `Unmapped`, `ZeroRecipients`
 * and `Unresolved` are first-class states, and an event stranded at `Resolved` after a failed
 * fan-out is visible here and is advanced by nothing.
 */
export const fetchNotificationEvents = (params) =>
  api.get("/notifications/events", { params }).then((r) => r.data.data);

/** One event, its fan-out and its suppression decision — including the recorded inference basis. */
export const fetchNotificationEvent = (id) =>
  api.get(`/notifications/events/${id}`).then((r) => r.data.data);

/**
 * Notifications carrying a `Failed` channel, server-paged, reported ALONGSIDE the retry budget that
 * governs them — a failure count is meaningless without it. Filter: `channel`.
 */
export const fetchDeliveryFailures = (params) =>
  api.get("/notifications/failures", { params }).then((r) => r.data.data);

/**
 * `{ channel }`. The single bounded MANUAL re-queue of a `Failed` channel back to `Pending`.
 *
 * IT DISPATCHES NOTHING and contacts no provider — the dispatch runner would perform the send on its
 * next pass. `attemptCount`, `lastAttemptAt` and the audit history are all PRESERVED, so a manual
 * re-queue cannot be repeated into an unbounded budget. See DISPATCH_RUNNER_HAS_NO_CALLER.
 */
export const retryDispatch = (id, channel) =>
  api.post(`/notifications/${id}/retry`, { channel }).then((r) => r.data.data);

// ── The Module 13 contract (EP-18) — Master Admin ──────────────────────────

/**
 * Every catalogue row awaiting a Module 13 trigger, with recipients, channels and priority already
 * resolved. It asserts nothing about WHEN — Module 12 owns no timer for any business condition.
 */
export const fetchScheduledRows = () =>
  api.get("/notifications/scheduled-rows").then((r) => r.data.data);

// ═════════════════════════════════════════════════════════════════════════════
// Frozen vocabulary (`functions/src/constants/notificationEnums.js`)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * FIVE channels. In-App is the primary, guaranteed channel and needs no provider. The other four each
 * require one, and NO PROVIDER IS BOUND ANYWHERE IN V1 — an unbound channel is recorded `Skipped`
 * with a reason, never silently dropped.
 */
export const CHANNEL = {
  IN_APP: "In-App",
  EMAIL: "Email",
  SMS: "SMS",
  WHATSAPP: "WhatsApp",
  PUSH: "Push",
};

export const CHANNEL_VALUES = Object.values(CHANNEL);

/** The three a user may toggle. In-App is the inbox; Push has no delivery or address source in V1. */
export const CONFIGURABLE_CHANNELS = [CHANNEL.EMAIL, CHANNEL.SMS, CHANNEL.WHATSAPP];
export const NON_CONFIGURABLE_CHANNELS = [CHANNEL.IN_APP, CHANNEL.PUSH];

export const PRIORITY = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

export const PRIORITY_VALUES = Object.values(PRIORITY);

/**
 * SEVEN per-channel delivery states.
 *
 * `Delivered` is reachable only for In-App, or for an outbound channel where a provider delivery
 * receipt is configured — none is, so `Sent` is the terminal success for those. `Suppressed` and
 * `Skipped` WITHHOLD delivery and never discard the record.
 */
export const DELIVERY_STATE = {
  PENDING: "Pending",
  HELD: "Held",
  SENT: "Sent",
  DELIVERED: "Delivered",
  FAILED: "Failed",
  SUPPRESSED: "Suppressed",
  SKIPPED: "Skipped",
};

export const DELIVERY_STATE_VALUES = Object.values(DELIVERY_STATE);

/**
 * The frozen delivery map. Two shapes are load-bearing:
 *   `Failed → Pending` is the ONLY exit from Failed, reachable from the manual re-queue and from
 *     nothing else. There is no automatic retry anywhere in the module.
 *   `Suppressed` and `Skipped` have NO exits — a withheld message is never re-sent.
 */
export const DELIVERY_STATE_TRANSITIONS = {
  Pending: ["Held", "Sent", "Skipped", "Failed"],
  Held: ["Suppressed", "Pending"],
  Sent: ["Delivered"],
  Delivered: [],
  Failed: ["Pending"],
  Suppressed: [],
  Skipped: [],
};

/** States that mean "this was not delivered to the inbox" — the inbox excludes exactly these. */
export const NOT_IN_INBOX_STATES = [
  DELIVERY_STATE.SUPPRESSED,
  DELIVERY_STATE.SKIPPED,
  DELIVERY_STATE.HELD,
];

export const READ_STATE = { UNREAD: "Unread", READ: "Read" };
export const READ_STATE_VALUES = Object.values(READ_STATE);

export const ARCHIVE_STATE = { ACTIVE: "Active", ARCHIVED: "Archived" };
export const ARCHIVE_STATE_VALUES = Object.values(ARCHIVE_STATE);

/**
 * SEVEN event states. An event is recorded for EVERY inbound call, including one that binds to no
 * catalogue row or resolves to zero recipients — otherwise a routing defect would be invisible.
 * `Unmapped`, `ZeroRecipients` and `Unresolved` are first-class recorded outcomes, not exceptions.
 */
export const EVENT_STATE = {
  RECEIVED: "Received",
  CLASSIFIED: "Classified",
  UNMAPPED: "Unmapped",
  RESOLVED: "Resolved",
  ZERO_RECIPIENTS: "ZeroRecipients",
  FANNED_OUT: "FannedOut",
  UNRESOLVED: "Unresolved",
};

export const EVENT_STATE_VALUES = Object.values(EVENT_STATE);

/** The three states that mean a message was never routed to anyone. */
export const EVENT_DEFECT_STATES = [
  EVENT_STATE.UNMAPPED,
  EVENT_STATE.ZERO_RECIPIENTS,
  EVENT_STATE.UNRESOLVED,
];

export const EVENT_STATE_TERMINAL = [
  EVENT_STATE.UNMAPPED,
  EVENT_STATE.UNRESOLVED,
  EVENT_STATE.ZERO_RECIPIENTS,
  EVENT_STATE.FANNED_OUT,
];

/** The ten recipient groups. Two are resolved by ruling rather than by a department. */
export const RECIPIENT_GROUP = {
  MASTER_ADMIN: "MasterAdmin",
  DEPARTMENT_ADMIN: "DepartmentAdmin",
  PROPERTIES_TEAM: "PropertiesTeam",
  FIELD_WORKER: "FieldWorker",
  LEGAL_TEAM: "LegalTeam",
  REGISTRATION_TEAM: "RegistrationTeam",
  SALES_EXECUTIVE: "SalesExecutive",
  CUSTOMER_SUPPORT: "CustomerSupport",
  PAYMENT_AUTHORITY: "PaymentAuthority",
  CUSTOMER: "Customer",
};

export const RECIPIENT_GROUP_VALUES = Object.values(RECIPIENT_GROUP);

/** How the four inherited one-message-per-business-fact rules become executable. */
export const SUPPRESSION_CLASS = {
  PRIMARY: "primary",
  SUBORDINATE: "subordinate",
  INDEPENDENT: "independent",
};

export const SUPPRESSION_CLASS_VALUES = Object.values(SUPPRESSION_CLASS);

/** Governs co-owner fan-out: a co-owner is notified about the ASSET, never about the PERSON. */
export const PERSONAL_CLASS = {
  PERSONAL: "personal",
  ASSET: "asset",
  DERIVED: "derived",
  OPERATIONAL: "operational",
};

export const PERSONAL_CLASS_VALUES = Object.values(PERSONAL_CLASS);

export const DIGEST_MODE = { INSTANT: "instant", DAILY: "daily" };
export const DIGEST_MODE_VALUES = Object.values(DIGEST_MODE);

/** FIVE skip reasons. A skipped channel is NOT a failure and is never retried. */
export const SKIP_REASON = {
  CHANNEL_NOT_CONFIGURED: "ChannelNotConfigured",
  PREFERENCE_DISABLED: "PreferenceDisabled",
  NO_ADDRESS: "NoAddress",
  QUIET_HOURS_DEFERRED: "QuietHoursDeferred",
  PUSH_UNAVAILABLE: "PushUnavailable",
};

export const SKIP_REASON_LABEL = {
  ChannelNotConfigured: "No provider is bound for this channel",
  PreferenceDisabled: "The recipient turned this channel off",
  NoAddress: "No address exists for this recipient on this channel",
  QuietHoursDeferred: "Released past its quiet-hours window",
  PushUnavailable: "Push has no address source in this version",
};

export const TRIGGER_SOURCE = { SEAM: "seam", SCHEDULED: "scheduled" };

/** The one event this module originates itself — it has no catalogue row and must not have one. */
export const EXCEPTION_EVENT_NAME = "notification.exception";

// ═════════════════════════════════════════════════════════════════════════════
// Recorded backend limitations — surfaced, never worked around
// ═════════════════════════════════════════════════════════════════════════════

/**
 * ⚠ THE DISPATCH RUNNER HAS NO CALLER ANYWHERE IN THE BACKEND.
 *
 * `runDispatchCycle` runs only when something calls it, and `index.js` wires the dispatch service's
 * audit seam without ever invoking it — there is no timer, cron, scheduler or module-load side effect
 * in the module by design. Three consequences follow, and every one of them is visible in the UI:
 *
 *   • An In-App channel is written `Pending` at fan-out and stays `Pending`. It is nevertheless IN
 *     the inbox — the visibility rule excludes Suppressed, Skipped and Held, not Pending — so the
 *     inbox works correctly while the delivery state never advances to Sent or Delivered.
 *   • No outbound channel is ever sent, which is moot in V1 anyway: no provider is bound.
 *   • The manual re-queue moves a Failed channel to `Pending`, where it waits for a runner that
 *     never comes. The action still has a purpose — it is the recorded, audited decision to try
 *     again — but the panel says plainly that no send follows it.
 */
export const DISPATCH_RUNNER_HAS_NO_CALLER = true;

/**
 * ⚠ SUPPRESSION ADJUDICATION ALSO HAS NO CALLER, for the same reason.
 *
 * `adjudicateDueEvents` and the exception sweep are invoked by the same runner. A customer's
 * subordinate message therefore stays HELD and unadjudicated indefinitely, and the exception sweep
 * never raises an alert for an unmapped or zero-recipient event. Those events are still RECORDED and
 * are fully visible on the events page — which is why oversight there is the real safety net.
 */
export const ADJUDICATION_HAS_NO_CALLER = true;

/**
 * ✅ RESOLVED (D5). The catalogue filter now works over HTTP.
 *
 * This previously recorded that `listCatalogue` required `filters.awaitingModule13` to be a
 * JavaScript boolean while Express delivers every query-string value as a STRING, so
 * `?awaitingModule13=true` was refused with 422 "awaitingModule13 filter must be a boolean".
 *
 * It now decodes the string form and was verified against the running API: the unfiltered catalogue
 * returns 70 rows and `?awaitingModule13=true` returns the 5 rows carrying that flag. The settings
 * page still filters the rows it already holds — a deliberate UI choice for a 70-row list, not a
 * workaround for a broken parameter.
 */
export const UNUSABLE_FILTERS = [];

/**
 * ⚠ A NOTIFICATION CARRIES NO EVENT NAME.
 *
 * The record stores its rendered line, its priority, its channels and its read state — but the
 * originating event is a bare reference, and the read that would resolve it is Master-Admin only. A
 * non-Master recipient therefore cannot learn which business event produced their own message.
 * The inbox shows the rendered content, which is what the recipient is meant to read.
 */
export const NOTIFICATION_CARRIES_EVENT_NAME = false;

/**
 * ⚠ APPROVED MESSAGE WORDING DOES NOT EXIST YET — AND NOR DOES THE PROVISIONAL LINE.
 *
 * CORRECTED (D13). This previously said that where no approved wording exists "a neutral factual line
 * is rendered and the notification is flagged `wordingProvisional`". That is PD8.2's specification,
 * not the delivered behaviour: no renderer exists in the backend. `notificationService` leaves
 * `renderedContent`, `templateKey` and `wordingProvisional` unset, and dispatch passes the unset
 * value straight through.
 *
 * A notification created by the application therefore carries NO content at all. Seeded demonstration
 * rows do carry a line and the `wordingProvisional` flag — they were written by the seed script, not
 * by the module — which is why the panel renders content when present and a plain fallback when it is
 * not. Approved wording remains company ruling P2; the provisional renderer remains unbuilt.
 */
export const APPROVED_WORDING_EXISTS = false;

/**
 * ⚠ THERE IS NO TEMPLATE EDITOR, AND NO ENDPOINT FOR ONE.
 *
 * A catalogue row carries template KEYS per channel; the wording those keys resolve to is a company
 * artefact that lives outside this system. The keys are editable through the catalogue row; the text
 * is not, anywhere.
 */
export const TEMPLATE_TEXT_EDITABLE = false;

// ═════════════════════════════════════════════════════════════════════════════
// Predicates and derivations mirroring the service
// ═════════════════════════════════════════════════════════════════════════════
// Every one of these is COSMETIC. The service re-checks all of it on every request.

/** The In-App channel entry — the one that decides whether a notification is in the inbox at all. */
export const inAppChannel = (notification) =>
  (notification?.channels || []).find((c) => c.channel === CHANNEL.IN_APP) || null;

/**
 * The notification-level dispatch state, aggregated from its channels. Derived at read, never stored
 * — which is why it is computed here rather than expected on the record.
 *
 * The order is deliberate: a Failed channel is the fact worth surfacing even when another succeeded,
 * and Suppressed/Skipped come last because neither is a delivery.
 */
export function deriveDispatchState(notification) {
  const channels = notification?.channels || [];
  if (channels.length === 0) return null;
  const has = (state) => channels.some((c) => c.state === state);
  if (has(DELIVERY_STATE.FAILED)) return DELIVERY_STATE.FAILED;
  if (has(DELIVERY_STATE.PENDING)) return DELIVERY_STATE.PENDING;
  if (has(DELIVERY_STATE.HELD)) return DELIVERY_STATE.HELD;
  if (has(DELIVERY_STATE.DELIVERED)) return DELIVERY_STATE.DELIVERED;
  if (has(DELIVERY_STATE.SENT)) return DELIVERY_STATE.SENT;
  if (has(DELIVERY_STATE.SUPPRESSED)) return DELIVERY_STATE.SUPPRESSED;
  return DELIVERY_STATE.SKIPPED;
}

/** The channels of one notification that a manual re-queue would accept. */
export const failedChannels = (notification) =>
  (notification?.channels || []).filter((c) => c.state === DELIVERY_STATE.FAILED);

export const isUnread = (n) => n?.readState === READ_STATE.UNREAD;
export const isArchived = (n) => n?.archiveState === ARCHIVE_STATE.ARCHIVED;

/** Forward-only: both operations are no-ops once taken, and nothing reverses either. */
export const canMarkRead = (n) => Boolean(n) && n.readState === READ_STATE.UNREAD;
export const canArchive = (n) => Boolean(n) && n.archiveState === ARCHIVE_STATE.ACTIVE;

/** Only a Failed channel can be re-queued, and only by a Master Admin. */
export const canRetryChannel = (channelEntry) =>
  channelEntry?.state === DELIVERY_STATE.FAILED;

/** An event that reached nobody. These are the rows oversight exists to find. */
export const isEventDefect = (event) => EVENT_DEFECT_STATES.includes(event?.state);

/**
 * An event stranded at `Resolved` — its recipients were resolved but the fan-out write failed. It is
 * non-terminal, nothing in this module advances it, and it is visible only here.
 */
export const isStrandedEvent = (event) =>
  event?.state === EVENT_STATE.RESOLVED;

/** Whether a channel's preference entry is on, given that absent means "not configured". */
export function channelPreferenceOf(preference, channel) {
  const entry = (preference?.channelPreferences || []).find((p) => p.channel === channel);
  if (!entry) return { configured: false, enabled: null, digestMode: null };
  return {
    configured: true,
    enabled: entry.enabled === undefined ? null : entry.enabled,
    digestMode: entry.digestMode || null,
  };
}

/** One configuration item, by its identifier, from the described list. */
export const configItem = (items, id) => (items || []).find((i) => i.id === id) || null;

/**
 * PART 6b · PHASE 5 (NOTIFICATIONS = A) — where this notification leads, for me.
 *
 * THE SERVER DECIDES, NOT THIS FILE. The client never reads `notification.reference` and builds a
 * path from it: doing so would make a stored id the authority on navigation, and a reader could then
 * be aimed at a record they are not allowed to open. This asks the server, which checks that the
 * notification is mine AND that the module owning the referenced record authorizes me for it, and
 * returns a path only when both hold.
 *
 * `{ hasDestination: false }` is a normal answer — every notification written before this phase has
 * no reference, and so does every event outside the declared map. A 404 means the reference cannot be
 * honoured for me: unauthorized, deleted and never-existed are deliberately indistinguishable.
 */
export const fetchNotificationDestination = (id) =>
  api.get(`/notifications/${id}/destination`).then((r) => r.data.data);
